import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { GripVertical, Plus, Trash2, ArrowRight } from 'lucide-react';

export default function ChainSetupModal({ project, item, onClose, onSaved }) {
  const [departments, setDepartments] = useState([]);
  const [chain, setChain] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/departments', { params: { active_only: '1' } }).then(r => setDepartments(r.data));
    // Load existing chain
    api.get(`/projects/${project.id}`).then(r => {
      const chains = r.data.chains || [];
      const itemChain = item
        ? chains.filter(c => c.project_item_id === item.id)
        : chains.filter(c => !c.project_item_id);
      if (itemChain.length) {
        setChain(itemChain.sort((a,b) => a.stage_order - b.stage_order).map(c => ({
          department_id: c.department_id, dept_name: c.dept_name, dept_color: c.dept_color
        })));
      } else {
        // Default: all 15 departments in order
        setChain([]);
      }
    });
  }, []);

  const addDept = (dept) => {
    if (chain.find(c => c.department_id === dept.id)) return toast.error('Yeh department already hai');
    setChain([...chain, { department_id: dept.id, dept_name: dept.name, dept_color: dept.color }]);
  };

  const removeDept = (idx) => setChain(chain.filter((_,i) => i !== idx));

  const moveUp = (idx) => {
    if (idx === 0) return;
    const n = [...chain];
    [n[idx-1], n[idx]] = [n[idx], n[idx-1]];
    setChain(n);
  };

  const moveDown = (idx) => {
    if (idx === chain.length-1) return;
    const n = [...chain];
    [n[idx], n[idx+1]] = [n[idx+1], n[idx]];
    setChain(n);
  };

  const saveChain = async () => {
    if (!chain.length) return toast.error('Add at least 1 stage');
    setSaving(true);
    try {
      const stages = chain.map((c, i) => ({ department_id: c.department_id, stage_order: i + 1 }));
      await api.post(`/projects/${project.id}/chain`, {
        project_item_id: item?.id || null,
        stages
      });
      toast.success('Production chain saved! ✅');
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const unusedDepts = departments.filter(d => !chain.find(c => c.department_id === d.id));

  return (
    <div className="modal-backdrop">
      <div className="modal-scroll">
        <div className="modal-box" style={{maxWidth:720}}>

          {/* Header */}
          <div className="modal-header">
            <div>
              <h2 className="text-lg font-bold">🔗 Production Chain Setup</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                {item ? `Product: ${item.item_name}` : `Project: ${project.name} (all items)`}
              </p>
            </div>
            <button onClick={onClose} className="modal-close" type="button">✕</button>
          </div>

          {/* Body grid */}
          <div className="p-5 grid grid-cols-2 gap-4">

            {/* Left: chain order */}
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-3">Stage Order</p>
              {chain.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                  <p className="text-gray-400 text-sm">Add departments from the right panel</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {chain.map((c, idx) => (
                    <div key={c.department_id} className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2">
                      <span className="text-xs font-bold text-gray-400 w-5">{idx+1}</span>
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: c.dept_color }} />
                      <span className="text-sm font-medium text-gray-800 flex-1">{c.dept_name}</span>
                      <div className="flex gap-1">
                        <button onClick={() => moveUp(idx)} disabled={idx===0} className="text-gray-400 hover:text-gray-700 disabled:opacity-20 px-1">↑</button>
                        <button onClick={() => moveDown(idx)} disabled={idx===chain.length-1} className="text-gray-400 hover:text-gray-700 disabled:opacity-20 px-1">↓</button>
                        <button onClick={() => removeDept(idx)} className="text-red-400 hover:text-red-600 px-1"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                  ))}
                  {chain.length > 0 && (
                    <div className="flex items-center gap-1 text-xs text-gray-400 pt-1 pl-2">
                      <ArrowRight className="w-3 h-3" />
                      <span>Chain complete → Item 100%</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right: available depts */}
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-3">Available Departments</p>
              <div className="space-y-1.5">
                {unusedDepts.map(d => (
                  <button key={d.id} onClick={() => d.has_workers ? addDept(d) : null}
                    disabled={!d.has_workers}
                    title={!d.has_workers ? 'No workers assigned to this department' : ''}
                    className={`w-full flex items-center gap-2 text-left border rounded-xl px-3 py-2 transition-colors ${
                      d.has_workers
                        ? 'border-gray-100 hover:border-amber-300 hover:bg-amber-50 cursor-pointer'
                        : 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                    }`}>
                    <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: d.color }} />
                    <span className="text-sm text-gray-700 flex-1">{d.name}</span>
                    {d.has_workers ? <Plus className="w-3.5 h-3.5 text-gray-400" /> : <span className="text-xs text-gray-400">No workers</span>}
                  </button>
                ))}
                {unusedDepts.length === 0 && (
                  <p className="text-xs text-gray-400 text-center py-4">All departments added ✅</p>
                )}
              </div>
            </div>

          </div>{/* end grid */}

          {/* Footer */}
          <div className="modal-footer">
            <button onClick={onClose} className="btn-secondary" style={{flex:1}}>
              Cancel
            </button>
            <button onClick={saveChain} disabled={saving || !chain.length}
              className="btn-primary" style={{flex:1}}>
              {saving ? 'Saving...' : `Save Chain (${chain.length} stages)`}
            </button>
          </div>

        </div>{/* end modal-box */}
      </div>{/* end modal-scroll */}
    </div>
  );
}
